import {
  ArrowLeftIcon,
  CameraIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { VEHICLES_COLLECTION_ENDPOINT } from '../../services/collectionEndpoints';

interface VINDecodeResult {
  vin: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  body_style?: string;
  engine?: string;
  transmission?: string;
  fuel_type?: string;
  drivetrain?: string;
}

interface VINDecodeApiResponse {
  success: boolean;
  vin: string;
  decoded_data?: Partial<VINDecodeResult>;
}

const MobileVINScanner: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicleId');

  const [scanning, setScanning] = useState(false);
  const [manualVin, setManualVin] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [decodedData, setDecodedData] = useState<VINDecodeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<'pending' | 'granted' | 'denied'>(
    'pending'
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Check camera permission on mount
  useEffect(() => {
    checkCameraPermission();
    return () => {
      stopCamera();
    };
  }, []);

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      setCameraPermission(
        result.state === 'granted' ? 'granted' : result.state === 'denied' ? 'denied' : 'pending'
      );
    } catch (error) {
      // Permissions API not supported, try to access camera directly
      setCameraPermission('pending');
    }
  };

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setScanning(true);
      setCameraPermission('granted');
    } catch (error: any) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError') {
        setCameraPermission('denied');
        setError(
          'Camera access denied. Please enable camera permissions in your browser settings.'
        );
      } else {
        setError('Failed to access camera. Please try again or enter VIN manually.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const decodeVIN = async (vin: string) => {
    if (vin.length !== 17) {
      setError('VIN must be exactly 17 characters');
      return;
    }

    // Basic VIN validation
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/i;
    if (!vinRegex.test(vin)) {
      setError('Invalid VIN format. VINs cannot contain I, O, or Q.');
      return;
    }

    try {
      setDecoding(true);
      setError(null);

      // Call VIN decode API
      const formData = new FormData();
      formData.append('vin', vin.toUpperCase());

      const response = await apiService.post<VINDecodeApiResponse>(
        '/vehicles/decode-vin',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response?.success) {
        const decoded = response.decoded_data ?? {};
        setDecodedData({
          vin: response.vin,
          year: decoded.year ?? 0,
          make: decoded.make ?? 'Unknown',
          model: decoded.model ?? 'Unknown',
          trim: decoded.trim,
          body_style: decoded.body_style,
          engine: decoded.engine,
          transmission: decoded.transmission,
          fuel_type: decoded.fuel_type,
          drivetrain: decoded.drivetrain,
        });
        toast.success('VIN decoded successfully!');
      } else {
        throw new Error('No data returned');
      }
    } catch (error: any) {
      console.error('VIN decode error:', error);
      setError('Failed to decode VIN. Please verify the VIN and try again.');

      // Create partial data with just the VIN
      setDecodedData({
        vin: vin.toUpperCase(),
        year: 0,
        make: 'Unknown',
        model: 'Unknown',
      });
    } finally {
      setDecoding(false);
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualVin.trim()) {
      decodeVIN(manualVin.trim());
    }
  };

  const handleUpdateVehicle = async () => {
    if (!decodedData || !vehicleId) return;

    try {
      await apiService.patch(`/vehicles/${vehicleId}`, {
        vin: decodedData.vin,
        year: decodedData.year > 0 ? decodedData.year : undefined,
        make: decodedData.make !== 'Unknown' ? decodedData.make : undefined,
        model: decodedData.model !== 'Unknown' ? decodedData.model : undefined,
        trim: decodedData.trim,
        body_style: decodedData.body_style,
        engine: decodedData.engine,
        transmission: decodedData.transmission,
        fuel_type: decodedData.fuel_type,
        drivetrain: decodedData.drivetrain,
      });

      toast.success('Vehicle updated successfully!');
      navigate(`/mobile/vehicle/${vehicleId}`);
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      toast.error('Failed to update vehicle');
    }
  };

  const handleCreateVehicle = async () => {
    if (!decodedData) return;

    try {
      const response = await apiService.post<{ id: string }>(VEHICLES_COLLECTION_ENDPOINT, {
        vin: decodedData.vin,
        year: decodedData.year > 0 ? decodedData.year : new Date().getFullYear(),
        make: decodedData.make !== 'Unknown' ? decodedData.make : 'Unknown',
        model: decodedData.model !== 'Unknown' ? decodedData.model : 'Unknown',
        trim: decodedData.trim,
        body_style: decodedData.body_style,
        engine: decodedData.engine,
        transmission: decodedData.transmission,
        fuel_type: decodedData.fuel_type,
        drivetrain: decodedData.drivetrain,
        status: 'available',
      });

      if (response?.id) {
        toast.success('Vehicle created successfully!');
        navigate(`/mobile/vehicle/${response.id}`);
      }
    } catch (error) {
      console.error('Failed to create vehicle:', error);
      toast.error('Failed to create vehicle');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-purple-600 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="text-center flex-1">
            <h1 className="font-semibold text-lg">VIN Scanner</h1>
          </div>
          <div className="w-10"></div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Camera Scanner Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <CameraIcon className="h-5 w-5 mr-2 text-gray-500" />
              Scan VIN Barcode
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Point your camera at the VIN barcode (usually on the dashboard or door jamb)
            </p>
          </div>

          <div className="relative">
            {scanning ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full aspect-video object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />

                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4/5 h-16 border-2 border-yellow-400 rounded-lg">
                    <div className="w-full h-full relative overflow-hidden">
                      <div className="absolute inset-0 border-l-4 border-yellow-400 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={stopCamera}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="p-8 text-center">
                {cameraPermission === 'denied' ? (
                  <>
                    <ExclamationTriangleIcon className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                    <p className="text-gray-600 mb-4">
                      Camera access is required to scan barcodes. Please enable camera permissions
                      in your browser settings.
                    </p>
                  </>
                ) : (
                  <>
                    <QrCodeIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">Tap the button below to start scanning</p>
                    <button
                      onClick={startCamera}
                      className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium"
                    >
                      Start Camera
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-t border-red-100">
              <p className="text-sm text-red-600 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                {error}
              </p>
            </div>
          )}
        </div>

        {/* Manual Entry Section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <DocumentMagnifyingGlassIcon className="h-5 w-5 mr-2 text-gray-500" />
              Enter VIN Manually
            </h3>
          </div>

          <form onSubmit={handleManualSubmit} className="p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={manualVin}
                onChange={(e) => setManualVin(e.target.value.toUpperCase())}
                placeholder="Enter 17-character VIN"
                maxLength={17}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-mono uppercase focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                type="submit"
                disabled={manualVin.length !== 17 || decoding}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {decoding ? 'Decoding...' : 'Decode'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">{manualVin.length}/17 characters</p>
          </form>
        </div>

        {/* Decoded VIN Results */}
        {decodedData && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-green-50">
              <h3 className="font-semibold text-green-800 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                VIN Decoded Successfully
              </h3>
            </div>

            <div className="p-4 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">VIN</p>
                <p className="font-mono text-sm">{decodedData.vin}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Year</p>
                  <p className="font-medium">{decodedData.year > 0 ? decodedData.year : '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Make</p>
                  <p className="font-medium">{decodedData.make}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Model</p>
                  <p className="font-medium">{decodedData.model}</p>
                </div>
                {decodedData.trim && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Trim</p>
                    <p className="font-medium">{decodedData.trim}</p>
                  </div>
                )}
                {decodedData.body_style && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Body Style</p>
                    <p className="font-medium">{decodedData.body_style}</p>
                  </div>
                )}
                {decodedData.engine && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Engine</p>
                    <p className="font-medium text-sm">{decodedData.engine}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                {vehicleId ? (
                  <button
                    onClick={handleUpdateVehicle}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium"
                  >
                    Update Vehicle
                  </button>
                ) : (
                  <button
                    onClick={handleCreateVehicle}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium"
                  >
                    Create New Vehicle
                  </button>
                )}
                <button
                  onClick={() => {
                    setDecodedData(null);
                    setManualVin('');
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 rounded-xl p-4">
          <h4 className="font-medium text-blue-900 mb-2">Tips for Scanning</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>Ensure good lighting on the barcode</li>
            <li>Hold your phone steady and close to the barcode</li>
            <li>
              VIN barcodes are usually found on the dashboard (visible through windshield) or
              driver's door jamb
            </li>
            <li>If scanning fails, enter the VIN manually above</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MobileVINScanner;
